import { useForm } from "@tanstack/react-form";
import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useEffect, useState, useTransition } from "react";
import { useDropzone } from "react-dropzone";
import {
	graphql,
	useFragment,
	useLazyLoadQuery,
	useMutation,
	useRefetchableFragment,
} from "react-relay";
import { toast } from "sonner";
import z from "zod";
import { DisabledScreenAlert } from "@/components/disabled-screen-alert";
import { Icon } from "@/components/icon";
import { CoursesTable } from "@/components/tables/courses";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CourseCategoryCreateDialog } from "@/dialogs/course-category-create-dialog";
import { ReorderCoursesDrawer } from "@/dialogs/reorder-courses-drawer";
import { useFileUpload } from "@/hooks/useFileUpload";
import { cn } from "@/utils";
import type { cursos_BatchJobs$key } from "./__generated__/cursos_BatchJobs.graphql";
import type { cursosAppearanceTab_club$key } from "./__generated__/cursosAppearanceTab_club.graphql";
import type { cursosBatchJobsQuery } from "./__generated__/cursosBatchJobsQuery.graphql";
import type { cursosChangeBatchAlertStatusMutation } from "./__generated__/cursosChangeBatchAlertStatusMutation.graphql";
import type { cursosContentTab_club$key } from "./__generated__/cursosContentTab_club.graphql";
import type { cursosQuery } from "./__generated__/cursosQuery.graphql";
import type { cursosUpdateAppearanceMutation } from "./__generated__/cursosUpdateAppearanceMutation.graphql";

const defaultValues = {
	view: "conteudo",
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/cursos/",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			view: z.enum(["aparencia", "conteudo"]).default("conteudo"),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

const originLabels: Record<string, string> = {
	HOTMART: "Hotmart",
	KIWIFY: "Kiwify",
	MEMBERKIT: "MemberKit",
};

function CourseImportAlerts({ club }: { club: cursos_BatchJobs$key }) {
	const [, startTransition] = useTransition();

	const [data, refetch] = useRefetchableFragment<
		cursosBatchJobsQuery,
		cursos_BatchJobs$key
	>(
		graphql`
			fragment cursos_BatchJobs on Club
				@refetchable(queryName: "cursosBatchJobsQuery")
			{
				courseImportJobs: batchJobs(type: COURSE_IMPORT, alertStatus: OPEN) {
					id
					status
					name
					completedTaskCount
					taskCount
					failedTaskCount
					... on CourseImportBatchJob {
						origin
					}
				}
				memberkitImportJobs: batchJobs(type: MEMBERKIT_IMPORT, alertStatus: OPEN) {
					id
					status
					name
					completedTaskCount
					taskCount
					failedTaskCount
					... on MemberKitBatchJob {
						origin
						course {
							name
						}
					}
				}
			}
		`,
		club,
	);

	const [changeBatchAlertStatus] =
		useMutation<cursosChangeBatchAlertStatusMutation>(
			graphql`
				mutation cursosChangeBatchAlertStatusMutation(
					$jobId: String!
					$isClosed: Boolean!
				) {
					changeBatchAlertStatus(jobId: $jobId, isClosed: $isClosed) {
						id
						alertClosed
					}
				}
			`,
		);

	const jobs = [
		...data.courseImportJobs.map((job) => ({
			...job,
			courseName: undefined as string | undefined,
		})),
		...data.memberkitImportJobs.map((job) => ({
			...job,
			courseName: job.course?.name ?? undefined,
		})),
	];
	const hasPendingJobs = jobs.some((job) => job.status === "PENDING");

	useEffect(() => {
		if (!hasPendingJobs) return;
		const interval = setInterval(() => {
			startTransition(() => {
				refetch({}, { fetchPolicy: "network-only" });
			});
		}, 10000);
		return () => clearInterval(interval);
	}, [hasPendingJobs, refetch]);

	const handleDismiss = (jobId: string) => {
		changeBatchAlertStatus({
			variables: { jobId, isClosed: true },
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				startTransition(() => {
					refetch({}, { fetchPolicy: "network-only" });
				});
			},
		});
	};

	if (jobs.length === 0) return null;

	return (
		<div className="flex flex-col gap-3 mb-4">
			{jobs.map((job) => {
				const originLabel = job.origin ? originLabels[job.origin] : undefined;

				if (job.status === "PENDING") {
					return (
						<Alert
							key={job.id}
							className="border-blue-600! bg-blue-50 dark:border-blue-800 dark:bg-blue-950"
						>
							<Spinner className="size-4 text-blue-600 dark:text-blue-400" />
							<AlertTitle>{`Importando curso${originLabel ? ` do ${originLabel}` : ""}`}</AlertTitle>
						</Alert>
					);
				}

				if (job.status === "COMPLETED") {
					return (
						<Alert
							key={job.id}
							className="border-green-700! bg-green-50 dark:border-green-800 dark:bg-green-950"
						>
							<AlertTitle className="flex items-center justify-between">
								<div className="flex flex-row items-center justify-start gap-3">
									<Icon
										name="CircleCheck"
										className="size-4 text-green-700! dark:text-green-400"
									/>
									<span>Importação concluída</span>
								</div>
								<Button
									variant="ghost"
									size="icon-sm"
									onClick={() => handleDismiss(job.id)}
								>
									<Icon name="X" />
								</Button>
							</AlertTitle>
							<AlertDescription>
								{job.courseName ? (
									<>
										{"O curso "}
										<strong>{job.courseName}</strong>
										{` foi importado com sucesso${originLabel ? ` do ${originLabel}` : ""}.`}
									</>
								) : (
									`O curso foi importado com sucesso${originLabel ? ` do ${originLabel}` : ""}.`
								)}
							</AlertDescription>
						</Alert>
					);
				}

				if (job.status === "FAILED") {
					return (
						<Alert key={job.id} variant="destructive">
							<AlertTitle className="flex items-center justify-between">
								<div className="flex flex-row items-center justify-start gap-3">
									<Icon name="CircleX" className="size-4" />
									<span>Falha na importação</span>
								</div>
								<Button
									variant="ghost"
									size="icon-sm"
									onClick={() => handleDismiss(job.id)}
								>
									<Icon name="X" />
								</Button>
							</AlertTitle>
							<AlertDescription>
								{`A importação do curso${originLabel ? ` do ${originLabel}` : ""} falhou. ${job.failedTaskCount} de ${job.taskCount} itens falharam.`}
							</AlertDescription>
						</Alert>
					);
				}

				return null;
			})}
		</div>
	);
}

function AppearanceTab({ club }: { club: cursosAppearanceTab_club$key }) {
	const { club: clubContext } = useRouteContext({ from: "/_logged/c/$club" });
	const { session } = useRouteContext({ from: "__root__" });

	const [isEditing, setIsEditing] = useState(false);
	const [isBackgroundHovering, setIsBackgroundHovering] = useState(false);
	const [isMobileBackgroundHovering, setIsMobileBackgroundHovering] =
		useState(false);

	const data = useFragment(
		graphql`
			fragment cursosAppearanceTab_club on Club {
				coursePage {
					appearance {
						backgroundImage {
							id
							signedUrl
						}
						mobileBackgroundImage {
							id
							signedUrl
						}
					}
				}
			}
		`,
		club,
	);

	const [updateAppearance] = useMutation<cursosUpdateAppearanceMutation>(
		graphql`
			mutation cursosUpdateAppearanceMutation(
				$club: String!
				$input: UpdateCourseAppearanceInput!
			) {
				updateCourseAppearance(
					club: $club
					updateCourseAppearanceInput: $input
				) {
					backgroundImage {
						id
						signedUrl
					}
					mobileBackgroundImage {
						id
						signedUrl
					}
				}
			}
		`,
	);

	const { upload } = useFileUpload({ club: clubContext?.slug ?? "", session });

	const appearance = data.coursePage.appearance;

	const form = useForm({
		defaultValues: {
			...(appearance?.backgroundImage
				? {
						backgroundImage: {
							id: appearance.backgroundImage.id,
							url: appearance.backgroundImage.signedUrl,
						},
					}
				: {}),
			...(appearance?.mobileBackgroundImage
				? {
						mobileBackgroundImage: {
							id: appearance.mobileBackgroundImage.id,
							url: appearance.mobileBackgroundImage.signedUrl,
						},
					}
				: {}),
		},
		validators: {
			onChange: z.object({
				backgroundImage: z
					.object({
						id: z.string(),
						url: z.string().url(),
					})
					.optional(),
				mobileBackgroundImage: z
					.object({
						id: z.string(),
						url: z.string().url(),
					})
					.optional(),
			}),
		},
		onSubmit: ({ value }) => {
			return new Promise((resolve, reject) => {
				updateAppearance({
					variables: {
						club: clubContext?.slug ?? "",
						input: {
							backgroundImageFile: value.backgroundImage?.id ?? null,
							mobileBackgroundImageFile:
								value.mobileBackgroundImage?.id ?? null,
						},
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						toast.success("Aparência atualizada com sucesso!");
						setIsEditing(false);
						resolve(response);
					},
					onError: (error) => {
						toast.error("Erro ao atualizar aparência. Tente novamente.");
						reject(error);
					},
				});
			});
		},
	});

	const {
		getRootProps: getBackgroundRootProps,
		getInputProps: getBackgroundInputProps,
		open: openBackgroundPicker,
	} = useDropzone({
		noClick: true,
		accept: { "image/*": [] },
		disabled: !isEditing,
		onDrop: ([file]) => {
			if (file) {
				upload(file).then((res) => {
					form.setFieldValue("backgroundImage", {
						id: res.globalId,
						url: res.url,
					});
				});
			}
		},
	});

	const {
		getRootProps: getMobileBackgroundRootProps,
		getInputProps: getMobileBackgroundInputProps,
		open: openMobileBackgroundPicker,
	} = useDropzone({
		noClick: true,
		accept: { "image/*": [] },
		disabled: !isEditing,
		onDrop: ([file]) => {
			if (file) {
				upload(file).then((res) => {
					form.setFieldValue("mobileBackgroundImage", {
						id: res.globalId,
						url: res.url,
					});
				});
			}
		},
	});

	return (
		<form
			className="overflow-hidden rounded-md border py-6 px-6 flex flex-col gap-6"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<div className="flex flex-row items-center justify-between">
				<CardTitle>Aparência da Página de Cursos</CardTitle>

				{isEditing ? (
					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.canSubmit]}
					>
						{([isSubmitting, canSubmit]) => (
							<Button type="submit" disabled={isSubmitting || !canSubmit}>
								{isSubmitting && <Spinner />}
								Salvar
							</Button>
						)}
					</form.Subscribe>
				) : (
					<Button
						type="button"
						variant="outline"
						onClick={() => setIsEditing(true)}
					>
						<Icon name="Pencil" />
						Editar
					</Button>
				)}
			</div>

			<div className="w-full pt-3 flex flex-col gap-6">
				<form.Field name="backgroundImage">
					{(field) => (
						<div className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
							<div className="flex flex-col gap-1.5 flex-1">
								<span className="text-sm text-foreground font-medium">
									Capa
								</span>
								<span className="text-sm text-muted-foreground">
									Dimensões Recomendadas: 1920x450px
								</span>
							</div>

							{/* biome-ignore lint/a11y/noStaticElementInteractions: expected */}
							<div
								onMouseEnter={() => setIsBackgroundHovering(true)}
								onMouseLeave={() => setIsBackgroundHovering(false)}
								className="relative w-full max-w-md"
								{...getBackgroundRootProps()}
							>
								<input {...getBackgroundInputProps()} className="hidden" />
								<div
									className={cn(
										"w-full h-28 rounded-md border border-dashed flex items-center justify-center bg-muted/50 overflow-hidden",
										isEditing && "cursor-pointer",
									)}
								>
									{field.state.value?.url ? (
										<img
											src={field.state.value.url}
											alt="Background"
											className="w-full h-full object-cover"
										/>
									) : (
										<Icon
											name="Image"
											className="size-8 text-muted-foreground"
										/>
									)}
								</div>
								{isEditing && isBackgroundHovering && (
									<button
										type="button"
										onClick={() => openBackgroundPicker()}
										className="inset-0 absolute w-full h-28 flex items-center justify-center border border-primary rounded-md bg-primary/10 text-primary border-dashed"
									>
										<Icon name="Upload" className="size-6" />
									</button>
								)}
							</div>
						</div>
					)}
				</form.Field>

				<Separator />

				<form.Field name="mobileBackgroundImage">
					{(field) => (
						<div className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
							<div className="flex flex-col gap-1.5 flex-1">
								<span className="text-sm text-foreground font-medium">
									Capa Mobile
								</span>
								<span className="text-sm text-muted-foreground">
									Dimensões Recomendadas: 750x400px
								</span>
							</div>

							{/* biome-ignore lint/a11y/noStaticElementInteractions: expected */}
							<div
								onMouseEnter={() => setIsMobileBackgroundHovering(true)}
								onMouseLeave={() => setIsMobileBackgroundHovering(false)}
								className="relative w-full max-w-56"
								{...getMobileBackgroundRootProps()}
							>
								<input
									{...getMobileBackgroundInputProps()}
									className="hidden"
								/>
								<div
									className={cn(
										"w-full aspect-[750/400] rounded-md border border-dashed flex items-center justify-center bg-muted/50 overflow-hidden",
										isEditing && "cursor-pointer",
									)}
								>
									{field.state.value?.url ? (
										<img
											src={field.state.value.url}
											alt="Mobile Background"
											className="w-full h-full object-cover"
										/>
									) : (
										<Icon
											name="Image"
											className="size-8 text-muted-foreground"
										/>
									)}
								</div>
								{isEditing && isMobileBackgroundHovering && (
									<button
										type="button"
										onClick={() => openMobileBackgroundPicker()}
										className="inset-0 absolute w-full aspect-[750/400] flex items-center justify-center border border-primary rounded-md bg-primary/10 text-primary border-dashed"
									>
										<Icon name="Upload" className="size-6" />
									</button>
								)}
							</div>
						</div>
					)}
				</form.Field>
			</div>
		</form>
	);
}

function ContentTab({
	club,
	onChanged,
}: {
	club: cursosContentTab_club$key;
	onChanged: () => void;
}) {
	const { club: clubContext } = useRouteContext({ from: "/_logged/c/$club" });
	const navigate = useNavigate();
	const [editingCategory, setEditingCategory] = useState<{
		id: string;
		name: string;
		orientation: "LANDSCAPE" | "PORTRAIT";
	} | null>(null);

	const data = useFragment(
		graphql`
			fragment cursosContentTab_club on Club {
				...reorderCoursesDrawer_club
				coursePage {
					categories(status: ALL) {
						id
						name
						active
						orientation
						position
						courses(status: ALL) {
							id
							name
							slug
							active
							visibility
							position
							classesCount
							analytics {
								totalSubscribers
							}
						}
					}
				}
			}
		`,
		club,
	);

	const categories =
		[...data.coursePage.categories]
			.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
			.map((category) => ({
				id: category.id,
				name: category.name,
				active: category.active,
				orientation: category.orientation,
				courses: [...category.courses]
					.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
					.map((course) => ({
						id: course.id,
						name: course.name,
						slug: course.slug,
						active: course.active,
						visibility: course.visibility,
						totalStudents: course.analytics.totalSubscribers,
						totalLessons: course.classesCount,
					})),
			})) ?? [];

	const handleEditCourse = (_id: string, slug: string) => {
		navigate({
			to: "/c/$club/area-de-membros/cursos/$slug",
			params: { club: clubContext?.slug ?? "", slug },
		});
	};

	const handleCreateCourse = (categoryId: string) => {
		navigate({
			to: "/c/$club/area-de-membros/cursos/$category/criar",
			params: { club: clubContext?.slug ?? "", category: categoryId },
		});
	};

	const handleImportCourse = (categoryId: string) => {
		navigate({
			to: "/c/$club/area-de-membros/cursos/$category/importar",
			params: { club: clubContext?.slug ?? "", category: categoryId },
		});
	};

	const handleEditCategory = (id: string) => {
		const cat = categories.find((c) => c.id === id);
		if (!cat) return;
		setEditingCategory({
			id: cat.id,
			name: cat.name,
			orientation: cat.orientation as "LANDSCAPE" | "PORTRAIT",
		});
	};

	return (
		<>
			<CoursesTable
				categories={categories}
				isPending={false}
				onEditCourse={handleEditCourse}
				onCreateCourse={handleCreateCourse}
				onImportCourse={handleImportCourse}
				onEditCategory={handleEditCategory}
				onDeleted={onChanged}
			/>
			<CourseCategoryCreateDialog
				club={clubContext?.slug ?? ""}
				open={!!editingCategory}
				onClose={() => setEditingCategory(null)}
				onSaved={onChanged}
				category={editingCategory}
			/>
		</>
	);
}

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { view } = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const [isReorderDrawerOpen, setIsReorderDrawerOpen] = useState(false);
	const [coursesFetchKey, setCoursesFetchKey] = useState(0);

	const data = useLazyLoadQuery<cursosQuery>(
		graphql`
			query cursosQuery($slug: String!) {
				club(slug: $slug) {
					id
					coursesEnabled: configuration(key: "COURSES_ENABLED")
					...cursosContentTab_club
					...cursosAppearanceTab_club
					...cursos_BatchJobs
					...reorderCoursesDrawer_club
				}
			}
		`,
		{ slug: club?.slug ?? "" },
		{ fetchPolicy: "store-and-network", fetchKey: coursesFetchKey },
	);

	const isDisabled = data.club.coursesEnabled === "0";

	return (
		<div className="p-6 flex flex-col gap-6">
			<CourseImportAlerts club={data.club} />

			{isDisabled && (
				<DisabledScreenAlert
					clubId={data.club.id}
					configKey="COURSES_ENABLED"
					featureName="Cursos"
				/>
			)}
			<Tabs
				defaultValue="conteudo"
				value={view}
				onValueChange={(val) =>
					navigate({
						search: (s) => ({ ...s, view: val as "conteudo" | "aparencia" }),
					})
				}
				className="gap-6"
			>
				<div className="flex items-center justify-between">
					<TabsList>
						<TabsTrigger value="conteudo" className="border-none">
							Conteúdo
						</TabsTrigger>
						<TabsTrigger value="aparencia" className="border-none">
							Aparência
						</TabsTrigger>
					</TabsList>
					{view === "conteudo" && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => setIsReorderDrawerOpen(true)}
						>
							<Icon name="ArrowUpDown" className="size-4" />
							Reordenar
						</Button>
					)}
				</div>
				<TabsContent value="conteudo">
					<ContentTab
						club={data.club}
						onChanged={() => setCoursesFetchKey((key) => key + 1)}
					/>
				</TabsContent>
				<TabsContent value="aparencia">
					<AppearanceTab club={data.club} />
				</TabsContent>
			</Tabs>
			<ReorderCoursesDrawer
				open={isReorderDrawerOpen}
				onOpenChange={setIsReorderDrawerOpen}
				club={data.club}
			/>
		</div>
	);
}
